import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, PhoneIcon, MessageCircleIcon, ArrowRightIcon, ArrowLeftIcon, MapPinIcon, StarIcon, CheckCircle2Icon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { TrustBar } from '../components/TrustBar';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { SectionLink } from '../components/SectionLink';
import { Seo } from '../components/Seo';
import { CONTACT } from '../data/copy';
import { getNeighborhood, neighborhoods } from '../data/neighborhoods';
import { SITE_URL, breadcrumbSchema, neighborhoodBusinessSchema } from '../data/schema';
import { useLanguage } from '../contexts/LanguageContext';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/5PB2T6CkyBda54JkDpJ8c2/Banner_Armando_Junk.png';
export function NeighborhoodPage({
  slug
}: {
  slug: string;
}) {
  const {
    lang,
    t
  } = useLanguage();
  const neighborhood = getNeighborhood(slug);
  const schemas = useMemo(() => neighborhood ? [neighborhoodBusinessSchema(neighborhood), breadcrumbSchema(neighborhood)] : [], [neighborhood]);
  if (!neighborhood) return null;
  const name = neighborhood.name;
  const content = neighborhood[lang];
  const fill = (template: string) => template.replace(/\{n\}/g, name);
  const others = neighborhoods.filter(item => item.slug !== slug);
  return <>
      <Seo title={neighborhood.seo.title} description={neighborhood.seo.description} keywords={`${name} junk removal, junk removal ${name}, ${name} hauling, junk hauling ${name}, same-day junk removal ${name}, ${name} furniture removal, ${name} garage cleanout, ${name} demolition services, affordable junk removal ${name}, licensed junk removal ${name}, junk removal San Francisco`} canonical={`${SITE_URL}/${neighborhood.slug}`} jsonLd={schemas} data-id="element-273" data-mp="21jpeck6su67b" />
      <Navbar data-id="element-274" data-mp="ikzv9sl2qft6" />

      <main data-id="element-275" data-mp="ikhpa48m0n82">
        {/* HERO */}
        <section className="relative isolate w-full overflow-hidden bg-forest-deep" data-id="element-276" data-mp="ii565nr486tf">
          <img src={HERO_IMAGE} alt={`Armando's junk removal truck loaded after a ${name} junk removal job in San Francisco`} className="absolute inset-0 h-full w-full object-cover object-[70%_center]" data-id="element-277" data-mp="ngymdnxsebxa" />
          <div className="absolute inset-0 bg-forest-deep/80 lg:bg-forest-deep/70" aria-hidden="true" data-id="element-278" data-mp="3vykz0o53up83" />
          <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-32 sm:pb-20 sm:pt-40 lg:px-8 lg:pb-24 lg:pt-44" data-id="element-279" data-mp="4li1szwu2edh">
            <nav aria-label={t.nbhd.breadcrumbAria} className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-semibold text-white/70" data-id="element-280" data-mp="3lyu4imgjoa80">
              <Link to="/" className="transition-colors duration-150 ease-out hover:text-rust-light" data-id="element-281" data-mp="3d0s70gw0rzph">
                {t.nbhd.crumbHome}
              </Link>
              <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-282" data-mp="2yfe51x4mdy1r" />
              <SectionLink hash="#services" className="transition-colors duration-150 ease-out hover:text-rust-light" data-id="element-283" data-mp="2tfrphh1bk5ps">
                {t.nbhd.crumbServices}
              </SectionLink>
              <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-284" data-mp="33er0v7dgyfgh" />
              <span aria-current="page" className="text-white" data-id="element-285" data-mp="3habafc7b5lb8">
                {fill(t.nbhd.crumbCurrent)}
              </span>
            </nav>

            <div className="max-w-2xl" data-id="element-286" data-mp="3r2h02fa37qn9">
              <p className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-rust-light" data-id="element-287" data-mp="3r1fp0at2n45j">
                <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-288" data-mp="3m58fk81gvaiu" />
                {name} · San Francisco, CA
              </p>
              <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl" data-id="element-289" data-mp="3v42jkjh4n36f">
                {fill(t.nbhd.h1)}
              </h1>
              <p className="mt-5 max-w-xl font-display text-lg font-bold text-white/90" data-id="element-290" data-mp="1tck7lj4ohwup">
                {fill(t.nbhd.heroHeadline)}
              </p>
              <p className="mt-3 max-w-xl text-lg leading-relaxed text-white/85" data-id="element-291" data-mp="23596zfmyfk9o">
                {fill(t.nbhd.heroSub)}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-292" data-mp="277vzzjvd3b8g">
                <SectionLink hash="#contact" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-rust px-7 py-4 font-display text-base font-bold text-white transition-colors duration-150 ease-out hover:bg-rust-dark" data-id="element-293" data-mp="1ahdaoc1arudf">
                  {t.nbhd.ctaQuote}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-294" data-mp="2uvm9bklp22sy" />
                </SectionLink>
                <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-md border-2 border-white/70 px-7 py-4 font-display text-base font-bold text-white transition-colors duration-150 ease-out hover:border-white hover:bg-white hover:text-forest" data-id="element-295" data-mp="2ztxl40vjundt">
                  <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-296" data-mp="306hoge674btv" />
                  {t.nbhd.ctaCall}
                </a>
              </div>
            </div>
          </div>
        </section>

        <TrustBar data-id="element-297" data-mp="2r97bmodqcemu" />

        {/* INTRO */}
        <section className="w-full bg-white py-20 lg:py-24" data-id="element-298" data-mp="2m89ywrbhocsq">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-16 lg:px-8" data-id="element-299" data-mp="2maw6us2akubh">
            <div data-id="element-300" data-mp="2d8ehw5v95nks">
              <h2 className="font-display text-3xl font-black tracking-tight text-forest sm:text-4xl" data-id="element-301" data-mp="2d8wonwfjjk6y">
                {fill(t.nbhd.introTitle)}
              </h2>
              <div className="mt-6 space-y-5" data-id="element-302" data-mp="bgryapdowuyc">
                {content.intro.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-[17px] leading-relaxed text-neutral-600" data-id="element-303" data-mp="24zh6nsf9sk8t">
                    {paragraph}
                  </p>)}
              </div>
            </div>

            <aside className="h-fit rounded-lg bg-forest p-8 lg:sticky lg:top-28" data-id="element-304" data-mp="1vwpqjfbg90au">
              <h3 className="font-display text-xl font-bold text-white" data-id="element-305" data-mp="200xehaa32c80">
                {fill(t.nbhd.finalCtaTitle)}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-white/75" data-id="element-306" data-mp="2sp30s1gsc99y">
                {t.nbhd.finalCtaBody}
              </p>
              <div className="mt-7 flex flex-col gap-3" data-id="element-307" data-mp="2eq3gt747wik4">
                <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2.5 rounded-md bg-rust px-5 py-3.5 font-display text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-rust-dark" data-id="element-308" data-mp="2ermy911m0znh">
                  <PhoneIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-309" data-mp="186ve2yv1ldr4" />
                  {t.nbhd.ctaCall}
                </a>
                <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2.5 rounded-md border-2 border-white/70 px-5 py-3 font-display text-sm font-bold text-white transition-colors duration-150 ease-out hover:border-white hover:bg-white hover:text-forest" data-id="element-310" data-mp="vr5iigywdarm">
                  <MessageCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-311" data-mp="c3el0z4dbaxx" />
                  {t.nbhd.ctaWhatsapp}
                </a>
              </div>
            </aside>
          </div>
        </section>

        {/* SERVICES */}
        <section className="w-full bg-sand py-20 lg:py-24" data-id="element-312" data-mp="3ufuihrnwmtbq">
          <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-313" data-mp="3ue9rgc0stsim">
            <h2 className="max-w-2xl font-display text-3xl font-black tracking-tight text-forest sm:text-4xl" data-id="element-314" data-mp="3pgq7qwlc0nja">
              {fill(t.nbhd.servicesTitle)}
            </h2>

            <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-315" data-mp="38whx0lekee1y">
              {t.nbhd.services.map(service => <li key={service.title} className="h-full" data-id="element-316" data-mp="3dut6uw4v0ok6">
                  <SectionLink hash="#services" className="group flex h-full flex-col rounded-lg border border-neutral-200 bg-white p-7 transition-[border-color,transform] duration-200 ease-out hover:-translate-y-1 hover:border-rust" data-id="element-317" data-mp="3iw0fsyiqntsf">
                    <h3 className="font-display text-lg font-bold text-forest" data-id="element-318" data-mp="gy6vv96xbr4">
                      {service.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-neutral-600" data-id="element-319" data-mp="5g1f1okgvy3b">
                      {fill(service.description)}
                    </p>
                    <span className="mt-auto flex items-center gap-2 pt-6 text-sm font-bold text-rust" data-id="element-320" data-mp="egr7ciair6ca">
                      {t.nbhd.viewAllServices}
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-321" data-mp="1z0mgdn7dvr5i" />
                    </span>
                  </SectionLink>
                </li>)}
            </ul>
          </div>
        </section>

        {/* LOCAL EXPERTISE */}
        <section className="w-full bg-forest py-20 lg:py-24" data-id="element-322" data-mp="2imhhs0la5739">
          <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-323" data-mp="2dnxv1xyv3r9u">
            <h2 className="max-w-2xl font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl" data-id="element-324" data-mp="2dqk2zypo08sl">
              {fill(t.nbhd.featuresTitle)}
            </h2>
            <ul className="mt-10 grid gap-px overflow-hidden rounded-lg bg-white/15 md:grid-cols-3" data-id="element-325" data-mp="23wa92cl2r2fr">
              {content.features.map(feature => <li key={feature.title} className="bg-forest p-7" data-id="element-326" data-mp="2wms6oy314bls">
                  <CheckCircle2Icon className="h-7 w-7 text-rust-light" aria-hidden="true" data-id="element-327" data-mp="2rom0csw3e7bh" />
                  <h3 className="mt-5 font-display text-lg font-bold text-white" data-id="element-328" data-mp="2mqsq9oro4t5x">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-white/70" data-id="element-329" data-mp="2wnos5bn2ah1i">
                    {feature.description}
                  </p>
                </li>)}
            </ul>
          </div>
        </section>

        {/* REVIEWS + COVERAGE */}
        <section className="w-full bg-white py-20 lg:py-24" data-id="element-330" data-mp="35ntxzhclx7dk">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8" data-id="element-331" data-mp="3kdsy3pnq2svo">
            <div data-id="element-332" data-mp="3fdn1rw6ici79">
              <h2 className="font-display text-3xl font-black tracking-tight text-forest sm:text-4xl" data-id="element-333" data-mp="3ff7v5ih35v3j">
                {fill(t.nbhd.reviewsTitle)}
              </h2>
              <figure className="mt-8 rounded-lg border border-neutral-200 p-7" data-id="element-334" data-mp="3pcmbzj6x8lwd">
                <div className="flex gap-1" aria-label={t.testimonials.ratingAria} data-id="element-335" data-mp="3pajao30bj6cc">
                  {Array.from({
                  length: 5
                }).map((_, index) => <StarIcon key={index} className="h-4 w-4 fill-rust text-rust" aria-hidden="true" data-id="element-336" data-mp="2ukmfusfn565" data-mpi={index} />)}
                </div>
                <blockquote className="mt-5 text-[15px] leading-relaxed text-neutral-700" data-id="element-337" data-mp="3p9wvvv707lex">
                  “{content.testimonial.quote}”
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3" data-id="element-338" data-mp="3g9gxh054b4gx">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest font-display text-sm font-bold text-white" data-id="element-339" data-mp="3gbbgua0qqoiw">
                    {content.testimonial.name.charAt(0)}
                  </span>
                  <span data-id="element-340" data-mp="3bae10437e6kk">
                    <span className="block font-display text-sm font-bold text-forest" data-id="element-341" data-mp="3banh8zyg4zd2">
                      {content.testimonial.name}
                    </span>
                    <span className="block text-xs text-neutral-500" data-id="element-342" data-mp="1gw08olofvhmf">
                      {name}
                    </span>
                  </span>
                </figcaption>
              </figure>
              <p className="mt-4 text-sm text-neutral-500" data-id="element-343" data-mp="17zhdf2sqjhmo">
                {fill(t.nbhd.reviewsNote)}
              </p>
            </div>

            <div data-id="element-344" data-mp="1ztb600qig0z8">
              <h2 className="font-display text-3xl font-black tracking-tight text-forest sm:text-4xl" data-id="element-345" data-mp="1zrqcmefxmo2y">
                {fill(t.nbhd.coverageTitle)}
              </h2>
              <p className="mt-6 text-[17px] leading-relaxed text-neutral-600" data-id="element-346" data-mp="1qrtqokkxg3bl">
                {fill(t.nbhd.coverageBody).replace('{streets}', neighborhood.streets.join(', '))}
              </p>
              <ul className="mt-6 flex flex-wrap gap-2" data-id="element-347" data-mp="fehkw45lxams">
                {neighborhood.streets.map(street => <li key={street} className="rounded-full bg-sand px-4 py-2 text-sm font-semibold text-forest" data-id="element-348" data-mp="agg27kc3fdbu">
                    {street}
                  </li>)}
              </ul>

              <h3 className="mt-10 font-display text-lg font-bold text-forest" data-id="element-349" data-mp="a7kjobsqm8bg">
                {t.nbhd.alsoServingTitle}
              </h3>
              <ul className="mt-4 flex flex-wrap gap-2" data-id="element-350" data-mp="f0vv6q8hvd9p">
                {others.map(item => <li key={item.slug} data-id="element-351" data-mp="j30mvjfdtztp">
                    <Link to={`/${item.slug}`} className="flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold text-forest transition-colors duration-150 ease-out hover:border-rust hover:text-rust" data-id="element-352" data-mp="nyxxra7vmdz3">
                      {item.name}
                      <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-353" data-mp="2us9xgkriqagh" />
                    </Link>
                  </li>)}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-bold" data-id="element-354" data-mp="tzoihndgoz2k">
                <Link to="/" className="flex items-center gap-2 text-forest transition-colors duration-150 ease-out hover:text-rust" data-id="element-355" data-mp="p14ulhfb5d7k">
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-356" data-mp="y3nw9y7cxjs8" />
                  {t.nbhd.backHome}
                </Link>
                <SectionLink hash="#services" className="text-forest transition-colors duration-150 ease-out hover:text-rust" data-id="element-357" data-mp="ada13yth74tf">
                  {t.nbhd.viewAllServices}
                </SectionLink>
                <SectionLink hash="#contact" className="text-forest transition-colors duration-150 ease-out hover:text-rust" data-id="element-358" data-mp="fblfmmupu3hz">
                  {t.nbhd.getEstimate}
                </SectionLink>
                <SectionLink hash="#contact" className="text-forest transition-colors duration-150 ease-out hover:text-rust" data-id="element-359" data-mp="3isk376xd8a17">
                  {t.nbhd.contactUs}
                </SectionLink>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="w-full bg-sand py-16 lg:py-20" data-id="element-360" data-mp="1u8o9hzmt6hsh">
          <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-361" data-mp="1u73fcb4ep0v5">
            <div className="rounded-lg bg-forest px-8 py-10 lg:flex lg:items-center lg:justify-between lg:gap-10 lg:px-12" data-id="element-362" data-mp="2hzto6bh0j417">
              <div className="max-w-xl" data-id="element-363" data-mp="2lzu4k1no9paw">
                <h2 className="font-display text-2xl font-black tracking-tight text-white sm:text-3xl" data-id="element-364" data-mp="280uovji50kqv">
                  {fill(t.nbhd.finalCtaTitle)}
                </h2>
                <p className="mt-3 text-[15px] leading-relaxed text-white/75" data-id="element-365" data-mp="2d1s4bobrj0oo">
                  {t.nbhd.finalCtaBody}
                </p>
              </div>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row lg:mt-0 lg:shrink-0" data-id="element-366" data-mp="11ljy9cx4h789">
                <SectionLink hash="#contact" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-rust px-6 py-3.5 font-display text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-rust-dark" data-id="element-367" data-mp="hinusqjtqyzp">
                  {t.nbhd.ctaQuote}
                </SectionLink>
                <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 border-white/70 px-6 py-3 font-display text-sm font-bold text-white transition-colors duration-150 ease-out hover:border-white hover:bg-white hover:text-forest" data-id="element-368" data-mp="3uxalanu8hjc6">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-369" data-mp="3gz40a8advdtx" />
                  {t.nbhd.ctaCall}
                </a>
                <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-whatsapp px-6 py-3.5 font-display text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-[#1DB456]" data-id="element-370" data-mp="1k2wrfavj80zo">
                  <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-371" data-mp="1wga7rndb07pt" />
                  {t.nbhd.ctaWhatsapp}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-372" data-mp="13t5yv7b3b98r" />
      <WhatsAppButton data-id="element-373" data-mp="18pwb1jjrxkow" />
    </>;
}